import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import type { ResolvedTheme, ThemePreference } from '../lib/types';

const STORAGE_KEY = '205.theme';       // must match the no-flash script in index.html

interface ThemeCtx {
  /** What the user chose. `system` follows the device. */
  theme: ThemePreference;
  /** What is actually on screen right now. */
  resolved: ResolvedTheme;
  setTheme: (t: ThemePreference) => void;
  /** Cycle light → dark → light, committing an explicit choice. */
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch { return 'system'; }
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Theme, persisted three ways on purpose:
 *   - `data-theme` on <html> so CSS resolves it,
 *   - `localStorage` so there is no flash of the wrong theme on next load,
 *   - the server, so the choice follows the user from phone to desktop.
 * Unset means "follow the device", which is the default.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readStored);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  // Track the device preference so `system` stays live rather than sampled once.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq) return;
    const onChange = () => setSystem(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = theme === 'system' ? system : theme;

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);

    // Keep the iOS status bar and Android chrome in step.
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
    if (meta) meta.content = resolved === 'light' ? '#F7F6F4' : '#0A0A0C';
  }, [theme, resolved]);

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    try {
      if (t === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, t);
    } catch { /* private mode: the session still works, it just will not persist */ }
    // Best effort — a failed preference save must never break the app.
    void api.setTheme(t).catch(() => undefined);
  }, []);

  const value = useMemo<ThemeCtx>(() => ({
    theme, resolved, setTheme,
    toggle: () => setTheme(resolved === 'dark' ? 'light' : 'dark'),
  }), [theme, resolved, setTheme]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme must be used inside <ThemeProvider>');
  return c;
}
